import { useMemo, useState, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation } from "react-router-dom";
import AddButton from "../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../components/EditButton";
import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import { Column } from "material-table";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { notificationFilterById } from "../../../../actions/Utilities/NotificationFilter";
import TableFilters from "components/TableFilters";
import TypeForm from "./forms/TypeForm";
import useGetType from "./hooks/useGetType";
import useDeleteTypes from "./hooks/useDeleteType";
import {
  addStateOfficeAppliancesType,
  editStateOfficeAppliancesType,
  officeAppliancesTypeFilterText,
  rowEditOfficeAppliancesType,
  selectedRowsStateOfficeAppliancesType,
} from "./atoms/atoms";
import {
  IOfficeAppliancesType,
  IOfficeAppliancesTypeTableData,
} from "./types/index";

export const OfficeAppliancesTypeTable = () => {
  const location = useLocation<{
    recordId?: string;
    moduleName?: string;
    subModuleName?: string;
  }>();

  const notifParams = location.state;

  const { data: typesData } = useGetType();
  const bulkDelete = useDeleteTypes();

  const [actionAddState, setActionAddState] = useRecoilState(
    addStateOfficeAppliancesType
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOfficeAppliancesType
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOfficeAppliancesType
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    officeAppliancesTypeFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditOfficeAppliancesType);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [tableData, setTableData] = useState<IOfficeAppliancesType[]>([]);
  const [tableFilteredData, setTableFilteredData] = useState<
    IOfficeAppliancesType[]
  >([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [recent, setRecent] = useState("");

  const addPermission =
    PERMISSIONS.permission_utilities_officeappliances_type_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeappliances_type_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_officeappliances_type_edit;

  useEffect(() => {
    if (Array.isArray(typesData)) {
      setTableData(typesData.map((d) => ({ ...d })));
    } else {
      setTableData([]);
    }
  }, [typesData]);

  const isLoading = !typesData;

  useEffect(() => {
    if (
      notifParams?.recordId &&
      notifParams?.moduleName &&
      notifParams?.subModuleName
    ) {
      const action = "VIEW";

      notificationFilterById(
        notifParams.recordId,
        notifParams.moduleName,
        notifParams.subModuleName,
        action
      ).then((result: IOfficeAppliancesType[] | undefined) => {
        if (Array.isArray(result) && result.length > 0) {
          setTableFilteredData(result);
          setRecent(result[0].officeApplianceType1 ?? "");
        } else {
          setTableFilteredData([]);
          setRecent("");
        }
      });
    } else {
      setRecent("");
      setTableFilteredData([]);
    }
    setSelectedRows([]);
  }, [
    notifParams?.recordId,
    notifParams?.moduleName,
    notifParams?.subModuleName,
    setSelectedRows,
  ]);

  const filteredItems = useMemo(() => {
    const search = filterTextState.trim().toLowerCase();
    if (search === "") return tableData;

    return tableData.filter((item) =>
      item.officeApplianceType1?.toLowerCase().includes(search)
    );
  }, [tableData, filterTextState]);

  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filteredItems);
    }
  }, [filterTextState, filteredItems, recent]);

  const handleAdd = () => {
    setActionAddState(true);
    setActionEditState(false);
    setEditRowData(null as any);
    setSelectedRows([]);
  };

  const handleSelectedRowsChange = (rows: IOfficeAppliancesTypeTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive && x.officeApplianceTypeId != null)
      .map((r) => {
        const { tableData, ...rest } = r;
        return rest;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IOfficeAppliancesTypeTableData) => {
    delete (rowData as any).tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
    setActionAddState(false);
  };

  const handleDelete = async () => {
    const ids = selectedRows?.map((row) => row.officeApplianceTypeId as number);

    if (ids.length === 0) return;

    bulkDelete(ids);
    setSelectedRows([]);
    setRecent("");
    setOpenModalDelete(false);
  };

  const deleteMessage = `Are you sure you want to deactivate ${rowsCount} ${
    rowsCount > 1 ? "Types" : "Type"
  }?`;

  const matColumns: Column<object>[] = [
    {
      title: "Type",
      field: "officeApplianceType1",
      customSort: (a: any, b: any) => {
        if (a.officeApplianceType1 && b.officeApplianceType1) {
          return a.officeApplianceType1
            .trim()
            .localeCompare(b.officeApplianceType1.trim());
        }
        if (a.officeApplianceType1) {
          return 0;
        }
        return -1;
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a: any, b: any) => {
        if (a.createdBy && b.createdBy) {
          return a.createdBy.trim().localeCompare(b.createdBy.trim());
        }
        if (a.createdBy) {
          return 0;
        }
        return -1;
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a: any, b: any) => {
        if (a.modifiedBy && b.modifiedBy) {
          return a.modifiedBy.trim().localeCompare(b.modifiedBy.trim());
        }
        if (a.modifiedBy) {
          return 0;
        }
        return -1;
      },
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData: any) => (rowData?.isActive ? "Active" : "Inactive"),
    },
    {
      title: "Action/s",
      align: "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData: any) => {
        const typedRow = rowData as IOfficeAppliancesTypeTableData;

        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => handleEdit(typedRow)}
            />
          </div>
        );
      },
    },
  ];

  let tableRows: IOfficeAppliancesType[] = [];

  if (isLoading) {
    tableRows = [];
  } else if (filterTextState === "" && recent === "") {
    tableRows = tableData;
  } else if (filterTextState === "") {
    tableRows = tableFilteredData;
  } else {
    tableRows = filteredItems;
  }

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <TypeForm setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"TypeName"}
              columnDetails={"Type"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />
            <div>
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Type"
                  onClick={handleAdd}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : null}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4" />
            )}
          </div>

          <div>
            <MaterialTableTS
              data={tableRows}
              showTitle={true}
              columns={matColumns}
              isLoading={isLoading}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Type"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => setOpenModalDelete(false)}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default OfficeAppliancesTypeTable;
